import { useState, useCallback, useRef, useMemo, useEffect } from "react";

const DEFAULT_QUERY_PARAMS = {
  page: 0,
  size: 10,
  status: "Active", // null | "Active" | "Inactive" | "Draft"
  promoType: null, // null | "smart" | "custom" | "buyer_campaign"
  search: "",
  sortField: null, // "discountValue" | "stayDates" | "bookingWindow" | "created" | "status"
  sortDirection: null, // "asc" | "desc"
};

export const usePromotionsQuery = (propertyId) => {
  const [queryParams, setQueryParams] = useState(DEFAULT_QUERY_PARAMS);
  const [searchInputValue, setSearchInputValue] = useState("");
  const searchTimeoutRef = useRef(null);

  // Build query string from current params
  const buildQueryString = useMemo(() => {
    if (!propertyId) return "";

    const params = new URLSearchParams();
    params.append("propertyId", propertyId);
    params.append("page", queryParams.page);
    params.append("size", queryParams.size);

    if (queryParams.status) {
      params.append("status", queryParams.status);
    }
    if (queryParams.promoType) {
      params.append("promoType", queryParams.promoType);
    }
    if (queryParams.search && queryParams.search.length >= 3) {
      params.append("search", queryParams.search);
    }
    if (queryParams.sortField && queryParams.sortDirection) {
      params.append(
        "sort",
        `${queryParams.sortField},${queryParams.sortDirection}`
      );
    }

    return `?${params.toString()}`;
  }, [propertyId, queryParams]);

  // Handle filter changes (status, type) - resets to page 0
  const handleFilterChange = useCallback((filterName, value) => {
    setQueryParams((prev) => ({
      ...prev,
      [filterName]: value || null,
      page: 0,
    }));
  }, []);

  // Handle search with debounce - resets to page 0
  const handleSearchChange = useCallback((value, immediate = false) => {
    setSearchInputValue(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (immediate) {
      setQueryParams((prev) => ({ ...prev, search: value, page: 0 }));
    } else {
      searchTimeoutRef.current = setTimeout(() => {
        setQueryParams((prev) => ({
          ...prev,
          search: value,
          page: 0,
        }));
      }, 800);
    }
  }, []);

  // Handle sort toggle
  const handleSortChange = useCallback((field) => {
    setQueryParams((prev) => {
      const isSameField = prev.sortField === field;
      const newDirection =
        isSameField && prev.sortDirection === "desc" ? "asc" : "desc";

      return {
        ...prev,
        sortField: field,
        sortDirection: newDirection,
        page: 0, // Reset to first page when sorting
      };
    });
  }, []);

  // Handle pagination - preserves all other params
  const handlePageChange = useCallback((page) => {
    setQueryParams((prev) => ({
      ...prev,
      page,
    }));
  }, []);

  // Reset all params
  const resetQueryParams = useCallback(() => {
    setQueryParams(DEFAULT_QUERY_PARAMS);
    setSearchInputValue("");
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  }, []);

  // Reset query params when property changes
  useEffect(() => {
    resetQueryParams();
  }, [propertyId, resetQueryParams]);

  return {
    queryParams,
    searchInputValue,
    buildQueryString,
    handleFilterChange,
    handleSearchChange,
    handleSortChange,
    handlePageChange,
    resetQueryParams,
  };
};
